import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { getArticle, groupedArticles, searchArticles } from "./docsContent";

/**
 * Docs navigation: a search box over all articles plus an audience-grouped
 * list. Mirrors the AdminSidebar layout — horizontal scroll on mobile,
 * vertical rail on desktop — so it reads as native app chrome.
 */
export function DocsSidebar({
  activeSlug,
  searchOnly = false,
}: {
  activeSlug?: string;
  searchOnly?: boolean;
}) {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => {
    const all = groupedArticles();
    if (!activeSlug) return all;
    const active = getArticle(activeSlug);
    return active ? all.filter((group) => group.audience.id === active.audience) : all;
  }, [activeSlug]);
  const results = useMemo(
    () => (query.trim().length >= 2 ? searchArticles(query) : null),
    [query],
  );

  return (
    <aside
      data-testid="docs-sidebar"
      data-search-only={searchOnly || undefined}
      className="docs-sidebar"
    >
      <form className="docs-search" onSubmit={(event) => event.preventDefault()}>
        <label htmlFor={searchOnly ? "docs-index-search" : "docs-rail-search"}>Search help</label>
        <div className="docs-search-controls">
          <div className="docs-search-input-wrap">
            <input
              id={searchOnly ? "docs-index-search" : "docs-rail-search"}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={'Try “feedback” or “password”'}
              aria-label="Search help articles"
              data-testid="docs-search-input"
            />
            {query ? (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="docs-search-clear">
                <X aria-hidden="true" />
              </button>
            ) : null}
          </div>
          <button type="submit" className="docs-search-submit">
            <Search aria-hidden="true" />
            Search
          </button>
        </div>
      </form>

      {results ? (
        <SearchResults query={query} hits={results} />
      ) : !searchOnly ? (
        <nav
          aria-label="Help topics"
          data-testid="docs-nav"
          className="docs-topic-nav"
        >
          {groups.map(({ audience, articles }) => (
            <div
              key={audience.id}
              className="docs-topic-group"
              data-docs-group={audience.id}
            >
              <h3>
                {audience.label}
              </h3>
              <ul>
                {articles.map((a) => (
                  <li key={a.slug}>
                    <Link
                      to="/docs/$slug"
                      params={{ slug: a.slug }}
                      data-testid={`docs-nav-${a.slug}`}
                      data-active={a.slug === activeSlug ? "true" : undefined}
                      className="docs-topic-link"
                    >
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      ) : null}
    </aside>
  );
}

function SearchResults({
  query,
  hits,
}: {
  query: string;
  hits: ReturnType<typeof searchArticles>;
}) {
  if (hits.length === 0) {
    return (
      <p
        className="docs-search-empty"
        data-testid="docs-search-empty"
      >
        No results for “{query}”.
      </p>
    );
  }
  return (
    <ul className="docs-search-results" data-testid="docs-search-results">
      {hits.map(({ article, snippet }) => (
        <li key={article.slug}>
          <Link
            to="/docs/$slug"
            params={{ slug: article.slug }}
            className={cn("docs-search-result")}
          >
            <span className="docs-search-result-title">
              {article.title}
            </span>
            <span className="docs-search-result-snippet">
              {snippet}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
